import React, { useState, useEffect, Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { FiX } from "react-icons/fi";
import { addResource, updateResource } from "../api/resourceApi";
import { toast } from "react-toastify";

const AddResourceModal = ({ isOpen, onClose, onResourceSaved, resourceToEdit }) => {
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (resourceToEdit) {
            setName(resourceToEdit.name);
            setDescription(resourceToEdit.description);
        } else {
            setName("");
            setDescription("");
        }
    }, [resourceToEdit, isOpen]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSaving(true);
        const resourceData = { name, description };

        try {
            if (resourceToEdit) {
                await updateResource(resourceToEdit.id, resourceData);
                toast.success("Resource updated successfully!");
            } else {
                await addResource(resourceData);
                toast.success("Resource added successfully!");
            }
            onResourceSaved();
            onClose();
        } catch (error) {
            toast.error("Failed to save resource.");
            console.error("Error saving resource:", error);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Transition appear show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-10" onClose={onClose}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black bg-opacity-25" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4 text-center">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                                <Dialog.Title
                                    as="h3"
                                    className="text-lg font-medium leading-6 text-gray-900 flex justify-between items-center"
                                >
                                    {resourceToEdit ? "Edit Resource" : "Add New Resource"}
                                    <button
                                        onClick={onClose}
                                        className="text-gray-400 hover:text-gray-600"
                                    >
                                        <FiX size={24} />
                                    </button>
                                </Dialog.Title>
                                <div className="mt-4">
                                    <form onSubmit={handleSubmit}>
                                        <div className="mb-4">
                                            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="name">
                                                Resource Name
                                            </label>
                                            <input
                                                type="text"
                                                id="name"
                                                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                                                value={name}
                                                onChange={(e) => setName(e.target.value)}
                                                required
                                            />
                                        </div>
                                        <div className="mb-4">
                                            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="description">
                                                Description
                                            </label>
                                            <textarea
                                                id="description"
                                                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                                                value={description}
                                                onChange={(e) => setDescription(e.target.value)}
                                                rows="3"
                                            />
                                        </div>
                                        <div className="flex justify-end mt-6">
                                            <button
                                                type="submit"
                                                className="bg-orange-500 hover:bg-orange-600 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline transition-colors duration-200"
                                                disabled={isSaving}
                                            >
                                                {isSaving ? "Saving..." : "Save Resource"}
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
};

export default AddResourceModal;